"use client";
import React, { useState, useEffect, useRef } from 'react';
import { X, HelpCircle } from 'lucide-react';

const FAQModal = ({ isOpen, onClose, faq, categories, onSave }) => {
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    status: 'Active'
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (faq) {
      setFormData({
        question: faq.question || '',
        answer: faq.answer || '',
        status: faq.status || 'Active'
      });
    } else {
      setFormData({
        question: '',
        answer: '',
        status: 'Active'
      });
    }
    setErrors({});
  }, [faq, isOpen]);

  // Handle escape key and focus management
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    } else if (formData.question.trim().length < 10) {
      newErrors.question = 'Question must be at least 10 characters';
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'Answer is required';
    } else if (formData.answer.trim().length < 20) {
      newErrors.answer = 'Answer must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Just send the form fields + id if editing
      const payload = faq
        ? {
            id: faq.id || faq._id, // so the page knows which one to update
            ...formData,
          }
        : {
            ...formData,
          };

      await onSave(payload);
      onClose();
    } catch (error) {
      console.error("Error saving FAQ:", error);
      setErrors({ general: "Failed to save FAQ. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col overflow-hidden transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Teal header */}
        <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
          <h2 id="modal-title" className="text-lg font-semibold text-white tracking-tight">
            {faq ? "Edit FAQ" : "Add New FAQ"}
          </h2>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="px-6 py-6 space-y-6"
        >
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* Question */}
          <div className="space-y-3">
            <label htmlFor="question" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
              Question *
            </label>
            <input
              type="text"
              id="question"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              placeholder="Enter the frequently asked question..."
              className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                errors.question ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'
              }`}
              disabled={isLoading}
              maxLength={200}
            />
            <div className="flex justify-between items-center">
              {errors.question ? (
                <p className="text-red-500 text-sm">{errors.question}</p>
              ) : (
                <p className="text-xs text-gray-600">Make it clear and specific</p>
              )}
              <span className="text-xs text-gray-600 font-medium">{formData.question.length}/200</span>
            </div>
          </div>

          {/* Answer */}
          <div className="space-y-3">
            <label htmlFor="answer" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
              Answer *
            </label>
            <textarea
              id="answer"
              name="answer"
              value={formData.answer}
              onChange={handleInputChange}
              placeholder="Provide a comprehensive answer to the question..."
              rows={6}
              className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 resize-none ${
                errors.answer ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'
              }`}
              disabled={isLoading}
              maxLength={1000}
            />
            <div className="flex justify-between items-center">
              {errors.answer ? (
                <p className="text-red-500 text-sm">{errors.answer}</p>
              ) : (
                <p className="text-xs text-gray-600">Provide a detailed and helpful answer</p>
              )}
              <span className="text-xs text-gray-600 font-medium">{formData.answer.length}/1000</span>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
              Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="Active"
                  checked={formData.status === 'Active'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.status === 'Active'
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 bg-white group-hover:border-green-400'
                }`}>
                  {formData.status === 'Active' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium transition-colors ${
                  formData.status === 'Active' ? 'text-green-700' : 'text-gray-700'
                }`}>
                  Active
                </span>
                <div className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  formData.status === 'Active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  Visible to customers
                </div>
              </label>

              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="Inactive"
                  checked={formData.status === 'Inactive'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.status === 'Inactive'
                    ? 'border-gray-500 bg-gray-500'
                    : 'border-gray-300 bg-white group-hover:border-gray-400'
                }`}>
                  {formData.status === 'Inactive' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium transition-colors ${
                  formData.status === 'Inactive' ? 'text-gray-700' : 'text-gray-700'
                }`}>
                  Inactive
                </span>
                <div className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  formData.status === 'Inactive'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  Hidden from customers
                </div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-8 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-white/80 text-gray-700 rounded-xl hover:bg-opacity-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 font-semibold border border-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                faq ? 'Update FAQ' : 'Create FAQ'
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default FAQModal;
